import EditGlobals from '@highcharts/dashboards/es-modules/Dashboards/EditMode/EditGlobals.js';
import EditMode from '@highcharts/dashboards/es-modules/Dashboards/EditMode/EditMode.js';
import { isRecord } from '@h20/contracts/utils/object';

const { accordionContent, accordionHeaderBtn, hiddenElement } = EditGlobals.classNames;
const GROUP_CONTENT_SELECTOR = `.${accordionContent}`;
const GROUP_HEADER_BUTTON_SELECTOR = `.${accordionHeaderBtn}`;

/** The board's edit mode controller, once it has one. */
export function editModeOf(board: unknown): EditMode | undefined {
    const editMode: unknown = isRecord(board) ? board['editMode'] : undefined;
    return editMode instanceof EditMode ? editMode : undefined;
}

/** Whether this board offers a cell's settings panel. */
export function canEditSettings(board: unknown): boolean {
    const options = editModeOf(board)?.options;
    return options?.enabled === true && options.settings?.enabled !== false;
}

/** Draws the open settings panel again, from the cell's current settings fields. */
export function redrawSettingsPanel(component: object): void {
    setTimeout(() => drawSettingsPanel(component), 0);
}

function drawSettingsPanel(component: object): void {
    const editMode = editModeOf('board' in component ? component.board : undefined);
    if (editMode === undefined) {
        return;
    }

    const cell: unknown = 'editCellContext' in editMode ? editMode.editCellContext : undefined;
    if (!isRecord(cell) || cell['mountedComponent'] !== component) {
        return;
    }

    const sidebar: unknown = editMode.sidebar;
    if (!isRecord(sidebar) || sidebar['isVisible'] !== true) {
        return;
    }
    if (typeof sidebar['generateContent'] !== 'function') {
        return;
    }
    // The panel element is what the open groups are read off, and a visible sidebar always has one.
    const panel: unknown = sidebar['container'];
    if (!(panel instanceof HTMLElement)) {
        return;
    }

    const rawMenu: unknown = sidebar['accordionMenu'];
    const menu = isRecord(rawMenu) ? rawMenu : undefined;

    const undoPoint: unknown = menu?.['oldOptionsBuffer'];
    const stalePopup: unknown = menu?.['confirmationPopup'];

    // Regenerating draws every group collapsed, so remember which ones the author had open.
    const openBefore = new Set(
        settingsGroups(panel)
            .filter((group) => !group.content.classList.contains(hiddenElement))
            .map((group) => group.key)
    );

    sidebar['generateContent'](cell);

    for (const group of settingsGroups(panel)) {
        if (openBefore.has(group.key) && group.content.classList.contains(hiddenElement)) {
            // The header's own click handler is what toggles the content and the chevron.
            group.header?.click();
        }
    }

    if (menu !== undefined && undoPoint !== undefined) {
        menu['oldOptionsBuffer'] = undoPoint;
    }
    const staleElement: unknown = isRecord(stalePopup) ? stalePopup['container'] : undefined;
    if (staleElement instanceof HTMLElement) {
        staleElement.remove();
    }
}

/**
 * The panel's collapsible groups, each with a key that still names the same group after a redraw. The
 * key is the group's header text, numbered when several groups share one, such as the `Card 1` and
 * `Card 2` a KPI widget repeats.
 */
function settingsGroups(panel: HTMLElement) {
    const used = new Map<string, number>();
    return [...panel.querySelectorAll(GROUP_CONTENT_SELECTOR)].map((content) => {
        const header = content.parentElement?.querySelector<HTMLElement>(
            GROUP_HEADER_BUTTON_SELECTOR
        );
        const text = header?.textContent?.trim() ?? '';
        const nth = used.get(text) ?? 0;
        used.set(text, nth + 1);
        return { key: `${text}#${nth}`, header, content };
    });
}
